import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, InputNumber, DatePicker, message, Space, Row, Col, Typography, Popconfirm, Tag, Card, Statistic } from 'antd';
import { PlusOutlined, DeleteOutlined, MoneyCollectOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { downloadCsv } from '../utils/exportCsv';

const { Title, Text } = Typography;
const { Option } = Select;

const payeeTypeLabel = { artist: 'Artist', company: 'Company', other: 'Other' };
const payeeTypeColor = { artist: 'purple', company: 'blue', other: 'default' };

export default function Royalties() {
  const [payees, setPayees] = useState([]);
  const [totals, setTotals] = useState({ owed: 0, paid: 0, balance: 0 });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [entriesModal, setEntriesModal] = useState(false);
  const [entries, setEntries] = useState([]);
  const [activePayee, setActivePayee] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => { fetchSummary(); fetchCompanies(); }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get('/royalties');
      setPayees(res.data.payees || []);
      setTotals(res.data.totals || { owed: 0, paid: 0, balance: 0 });
    } catch (err) { console.error(err); }
    setLoading(false);
  };
  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const handleSave = async (values) => {
    try {
      await api.post('/royalties', {
        ...values,
        entry_date: values.entry_date ? values.entry_date.format('YYYY-MM-DD') : undefined,
      });
      message.success('Entry added!');
      setModalVisible(false);
      form.resetFields();
      fetchSummary();
      if (entriesModal && activePayee) openEntries(activePayee);
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const openEntries = async (payee) => {
    setActivePayee(payee);
    setEntriesModal(true);
    try { const res = await api.get('/royalties/entries', { params: { payee } }); setEntries(res.data || []); }
    catch (err) { console.error(err); }
  };

  const deleteEntry = async (id) => {
    try { await api.delete(`/royalties/${id}`); message.success('Deleted'); fetchSummary(); if (activePayee) openEntries(activePayee); }
    catch (err) { message.error('Failed'); }
  };

  const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

  const columns = [
    { title: 'Payee', dataIndex: 'payee_name', key: 'payee', render: (v, r) => <Space><b>{v}</b><Tag color={payeeTypeColor[r.payee_type]}>{payeeTypeLabel[r.payee_type]}</Tag></Space> },
    { title: 'Total Owed', dataIndex: 'total_owed', key: 'owed', render: (v) => <Text>{inr(v)}</Text> },
    { title: 'Total Paid', dataIndex: 'total_paid', key: 'paid', render: (v) => <Text style={{ color: '#52c41a' }}>{inr(v)}</Text> },
    { title: 'Balance', dataIndex: 'balance', key: 'balance', render: (v) => <Text strong style={{ color: v > 0 ? '#cf1322' : '#52c41a' }}>{inr(v)}</Text> },
    {
      title: 'Actions', key: 'actions', width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openEntries(r.payee_name)}>Details</Button>
          <Button size="small" type="primary" onClick={() => { form.resetFields(); form.setFieldsValue({ payee_name: r.payee_name, payee_type: r.payee_type, entry_type: 'paid', entry_date: dayjs() }); setActivePayee(r.payee_name); setModalVisible(true); }}>Pay</Button>
        </Space>
      )
    }
  ];

  const entryColumns = [
    { title: 'Date', dataIndex: 'entry_date', key: 'date', render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '-' },
    { title: 'Type', dataIndex: 'entry_type', key: 'type', render: (v) => <Tag color={v === 'paid' ? 'green' : 'orange'}>{v === 'paid' ? 'Paid' : 'Owed'}</Tag> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => inr(v) },
    { title: 'Song', dataIndex: 'song_title', key: 'song', render: (v) => v || '-' },
    { title: 'Note', dataIndex: 'note', key: 'note', ellipsis: true, render: (v) => v || '-' },
    { title: '', key: 'del', width: 50, render: (_, r) => (
      <Popconfirm title="Delete this entry?" onConfirm={() => deleteEntry(r.id)}>
        <Button size="small" danger icon={<DeleteOutlined />} />
      </Popconfirm>
    ) }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><MoneyCollectOutlined style={{ color: '#722ed1' }} /> Royalty / Payouts</Title>
          <Text type="secondary">How much is owed to each artist/company, how much paid, and the balance</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/royalties/export', {})}>Download Excel</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ payee_type: 'artist', entry_type: 'owed', entry_date: dayjs() }); setActivePayee(null); setModalVisible(true); }}>Add Entry</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}><Card><Statistic title="Total Owed" value={totals.owed} precision={0} prefix="₹" /></Card></Col>
        <Col xs={24} sm={8}><Card><Statistic title="Total Paid" value={totals.paid} precision={0} prefix="₹" valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={24} sm={8}><Card><Statistic title="Balance" value={totals.balance} precision={0} prefix="₹" valueStyle={{ color: totals.balance > 0 ? '#cf1322' : '#52c41a' }} /></Card></Col>
      </Row>

      <Table dataSource={payees} columns={columns} rowKey="payee_name" loading={loading} pagination={{ pageSize: 20 }} scroll={{ x: 'max-content' }} locale={{ emptyText: 'No royalty entries yet. Click "Add Entry" to start.' }} />

      <Modal title="Add Royalty / Payment Entry" open={modalVisible} onCancel={() => setModalVisible(false)} onOk={() => form.submit()} okText="Save">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="payee_name" label="Payee (Artist / Company name)" rules={[{ required: true, message: 'Payee name required' }]}>
            <Input placeholder="e.g. Artist name or Company name" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="payee_type" label="Payee Type" initialValue="artist">
                <Select>
                  <Option value="artist">Artist</Option>
                  <Option value="company">Company</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="entry_type" label="Entry Type" initialValue="owed">
                <Select>
                  <Option value="owed">Owed</Option>
                  <Option value="paid">Paid</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true, message: 'Amount required' }]}>
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="entry_date" label="Date" initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="company_id" label="Company (optional)">
            <Select allowClear showSearch optionFilterProp="children" placeholder="Link to a company">
              {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={2} placeholder="e.g. Q1 royalty, advance payment, etc." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={`Entries — ${activePayee || ''}`} open={entriesModal} onCancel={() => setEntriesModal(false)} footer={null} width={760}>
        <Table dataSource={entries} columns={entryColumns} rowKey="id" size="small" pagination={{ pageSize: 10 }} />
      </Modal>
    </div>
  );
}
